// @flow
import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { destroy } from "redux-form";
import { useExperiment, emitter, experimentDebugger } from "@marvelapp/react-ab-test";
import { handleGtmAsyncEvent } from "Commons/Utilities/AsyncGTMHandler";

import { setLayout } from "redux/actions/layout";
import { getUser, getOffers } from "redux/sagas/selectors";
import { getSingleReminder } from "modules/Reminder/redux/reminders/actions";
import { fetchOffers } from "modules/Offers/redux/actions";
import { fromSlug } from "Commons/Modules/category";
import { isMobile } from "Commons/Utilities";
import { formatCurrency } from "modules/Offers/misc/Utils";

import LocalStorage from "Commons/Utilities/storage/LocalStorage";
import ChangeServiceForm from "modules/ChangeServiceForm/ChangeServiceForm";
import type { OfferT } from "modules/Offers/misc/Types";
import type { ReminderT } from "modules/Reminder/misc/Types";
import AgentMessage from "./components/AgentMessage/AgentMessage";
import OfferSummary from "./components/OfferSummary/OfferSummary";
import YearlySavings from "./components/YearlySavings/YearlySavings";
import Benefits from "./components/Benefits/Benefits";
import TariffComparison from "./components/TariffComparison/TariffComparison";
import Promise from "./components/Promise/Promise";
import NeedToKnow from "./components/NeedToKnow/NeedToKnow";
import Testimonials from "./components/Testimonials/Testimonials";
import CSFTitle from "./components/CSFTitle/CSFTitle";

import Styles from "./DetailPage.module.scss";

const OLD_VARIANT = "OfferDetailPageFloatingOfferBox";
const NEW_VARIANT = "OfferDetailPageGreenArrowTitle";
const TESTNAME = "offerDetailPageLayoutTest";

emitter.defineVariants(TESTNAME, [NEW_VARIANT, OLD_VARIANT], [50, 50]);
experimentDebugger.enable();
emitter.addWinListener((experimentName, variantName) => LocalStorage.set("utmExpid", variantName));

const DetailPage = () => {
  const { t } = useTranslation("offerPageDetail");
  const dispatch = useDispatch();
  const { selectVariant, activeVariant, emitWin } = useExperiment(TESTNAME);

  const {
    params: { reminderId, category },
  } = useRouteMatch();

  const variant = selectVariant({
    [NEW_VARIANT]: "newVariant",
    [OLD_VARIANT]: "oldVariant",
  });

  const offers = useSelector(getOffers);
  const offer: ?OfferT = offers?.offers?.[reminderId]?.[0];
  const remindMeBonus = offer?.remindMeBonus;
  const user = useSelector(getUser);

  let savingsFormated = formatCurrency(
    remindMeBonus?.amount ? remindMeBonus.totalSaving : offer?.gasElectricityDetails?.savings || 0
  );

  const reminder: ?ReminderT = useSelector(({ reminders }) => {
    return reminders.reminders.reminders?.find((reminder) => reminder.id === reminderId);
  });

  const categoryType = fromSlug(category);
  const savings = parseFloat(remindMeBonus ? remindMeBonus.totalSaving : offer?.gasElectricityDetails?.savings);
  const refOfferBox: { current: null | HTMLDivElement } = useRef(null);

  // Trigger event about which test won
  useEffect(() => {
    emitWin();
  }, []);

  useEffect(() => {
    handleGtmAsyncEvent("abTesting", activeVariant);
  }, [activeVariant]);

  useEffect(() => {
    dispatch(destroy("ChangeService"));
    dispatch(
      setLayout({
        header: { show: true, simple: false },
        sidenav: true,
        footer: true,
        breadcrumbs: false,
        pageType: "fullScreen",
        bgColor: "#f7f7f7",
      })
    );
    // dispatch(setShowPromotionBanner(true));
  }, [dispatch]);

  useEffect(() => {
    if (user.user?.id && reminderId) {
      dispatch(getSingleReminder(user.user.id, reminderId));
      dispatch(fetchOffers(user.user.id, reminderId));
    }
  }, [dispatch, user, reminderId]);

  const [isAtBottom, setIsAtBottom] = useState(false);

  const handleScroll = (scrollLimit, footer) => {
    if (footer) {
      const footerPos = footer.getBoundingClientRect().top;

      if (footerPos <= scrollLimit) {
        setIsAtBottom(true);
      } else {
        setIsAtBottom(false);
      }
    }
  };

  useEffect(() => {
    const mainLayout = document.getElementById("mainLayout");
    const footer = document.getElementById("footerNavigation");
    const scrollLimit = mainLayout && footer ? mainLayout.scrollHeight - footer.scrollHeight - 200 : 0;

    mainLayout?.addEventListener("scroll", () => handleScroll(scrollLimit, footer));
    return () => {
      mainLayout?.removeEventListener("scroll", () => handleScroll(scrollLimit, footer));
    };
  }, []);

  const desiredGreenPower =
    reminder?.electricityDesiredGreenPower === null
      ? reminder?.gasDesiredGreenPower
      : reminder?.electricityDesiredGreenPower;

  const CSdataFromReminder = {
    reminderId: reminder?.id,
    postalcode: reminder?.postalcode,
    locationId: reminder?.locationId,
    location: reminder?.location,
    locationMainName: reminder?.locationMainName,
    locationPartName: reminder?.locationPartName,
    yearlyConsumption: reminder?.electricityYearlyConsumption || reminder?.gasYearlyConsumption,
    ecoEnergy: desiredGreenPower ? "YES_ECO_TARIFF" : "NO_ECO_TARIFF",
    categoryProviderId: reminder?.categoryProviderId,
    permanentTariffId: reminder?.permanentTariffId,
    moveWithPreviousProvider: false,
    billingAddressSameAsDeliveryAddress: true,
    onBehalfOf: reminder?.onBehalfOf,
    switchProviderOrMove: "SWITCH_PROVIDER",
    switchTariffDateType: "NEXT_POSSIBLE_DATE",
    cancellationContractType: "AUTOMATIC_CANCELLATION",
    desiredPaymentMethod: "DIRECT_DEBIT_IBAN",
    accountOwnerType: "IMPORT_FROM_DELIVERY_ADDRESS",
    propertyType: reminder?.propertyType,
    hasContract: true,
    exceptionId: "1",
    cancelOldContract: "NEXT_POSSIBLE_DATE",
  };

  const CSFormData = {
    initialValues: {
      initialCategory: offer?.category,
      ELECTRICITY: {
        category: "ELECTRICITY",
        ...CSdataFromReminder,
      },
      GAS: {
        category: "GAS",
        ...CSdataFromReminder,
      },
    },
    formData: {
      COMMON: {
        savings: savingsFormated,
      },
    },
  };

  return (
    <>
      <div className={classnames(Styles.wrapper, Styles[variant])}>
        <AgentMessage offerExist={!!offer} categoryName={categoryType || ""} savings={savings} />
        {!!offer && !!reminder && (
          <>
            <div
              ref={refOfferBox}
              className={classnames(Styles.offerBox, {
                [Styles.fixedToBottom]: isAtBottom && variant !== "newVariant",
              })}
            >
              <OfferSummary reminder={reminder} offer={offer} email={user?.user?.email} category={category} />
            </div>
            {variant === "newVariant" && <CSFTitle />}
            <div className={Styles.CSFdesktopHolder}>
              <div id="CSF" className={Styles.CSFormWrapper}>
                {variant !== "newVariant" && <div className={Styles.title}>{t("changeServiceCTA")}</div>}
                <ChangeServiceForm
                  componentName={isMobile() ? "ChangeServiceForm_mobile_v1" : "ChangeServiceForm_initial"}
                  dataName={isMobile() ? "ChangeServiceForm_mobile_v1" : "ChangeServiceForm_initial"}
                  styleName={"ChangeServiceForm_initial"}
                  customization={{
                    data: JSON.stringify(CSFormData),
                  }}
                />
              </div>
            </div>
            <YearlySavings savings={savings} categoryType={categoryType} fullWidth={variant === "newVariant"} />
            {!isMobile() && <Benefits categoryType={categoryType} fullWidth={variant === "newVariant"} />}
            <TariffComparison
              reminder={reminder}
              offer={offer}
              remindMeBonus={remindMeBonus}
              category={category}
              fullWidth={variant === "newVariant"}
            />
            <Promise categoryType={categoryType || ""} fullWidth={variant === "newVariant"} />
            <NeedToKnow offer={offer} reminder={reminder} category={category} fullWidth={variant === "newVariant"} />
            <Testimonials offer={offer} reminder={reminder} category={category} fullWidth={variant === "newVariant"} />
          </>
        )}
      </div>
    </>
  );
};

export default DetailPage;
